import {
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  FunctionField,
  ReferenceField,
  useRecordContext,
  type ShowProps,
} from "react-admin";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatINR } from "@/utils";
import { formatListDate } from "@/utils/date-format";
import { formatFmbHijriPeriod, fromGregorian } from "@/utils/hijri-date-utils";
import dayjs from "dayjs";

type BookedSlotRow = {
  id?: string;
  bookedDate?: string;
  performanceMode?: "SELF" | "VAKALAT";
};

function BookedSlotsSection() {
  const record = useRecordContext();
  if (!record || record.contributionType !== "ZABIHAT") {
    return null;
  }
  const slots = (record.bookedSlots as BookedSlotRow[] | undefined) ?? [];
  const quantity = Number(record.quantity) || 0;

  return (
    <Box sx={{ mt: 2, width: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Zabihat dates — assigned {slots.length} of {quantity}
      </Typography>
      {slots.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No dates assigned.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Hijri</TableCell>
              <TableCell>Mode</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {slots.map((s, i) => {
              const d = s.bookedDate ? dayjs(s.bookedDate).toDate() : null;
              return (
                <TableRow key={s.id ?? `${s.bookedDate}-${i}`}>
                  <TableCell>{formatListDate(s.bookedDate, { empty: "—" })}</TableCell>
                  <TableCell>{d ? fromGregorian(d, "long") : "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={s.performanceMode === "VAKALAT" ? "Vakalat" : "Self"}
                      color={s.performanceMode === "VAKALAT" ? "secondary" : "info"}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

export default function FmbContributionsShow(props: ShowProps) {
  return (
    <Show {...props} title="FMB Contribution">
      <SimpleShowLayout>
        <ReferenceField source="fmbId" reference="fmbData" label="FMB" link="show">
          <TextField source="itsNo" />
        </ReferenceField>
        <TextField source="contributionType" label="Type" />
        <FunctionField
          label="Hijri period"
          render={(r) => formatFmbHijriPeriod(r?.hijriYearStart, null) ?? "—"}
        />
        <TextField source="beneficiaryItsNo" label="Beneficiary ITS" />
        <TextField source="beneficiaryName" label="Beneficiary name" />
        <NumberField source="quantity" label="Quantity" />
        <FunctionField
          label="Unit amount"
          render={(r) => formatINR(r?.unitAmount, { empty: "—" })}
        />
        <FunctionField label="Amount" render={(r) => formatINR(r?.amount, { empty: "—" })} />
        <FunctionField
          label="Pending"
          render={(r) => formatINR(r?.contributionPendingAmount, { empty: "—" })}
        />
        <TextField source="remarks" label="Remarks" />
        <BookedSlotsSection />
      </SimpleShowLayout>
    </Show>
  );
}
